import { useState, useCallback, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  Download,
  FileText,
  Volume2,
  Sparkles,
  ChevronDown,
  ChevronRight,
  FileDown,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { AudioPlayer } from '@/components/player/AudioPlayer'
import { TranscriptViewer } from '@/components/player/TranscriptViewer'
import { useTranscript, useTranscriptAudioUrl, useReprocessSummary } from '@/hooks/useTranscripts'
import { formatDateTime, formatDuration } from '@/lib/utils'

// Collapsible panel component
function CollapsiblePanel({
  title,
  icon: Icon,
  defaultOpen = false,
  children,
  badge,
}: {
  title: string
  icon: React.ElementType
  defaultOpen?: boolean
  children: React.ReactNode
  badge?: string | number
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border rounded-xl overflow-hidden bg-card shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">{title}</span>
          {badge && (
            <Badge variant="secondary" className="text-xs">
              {badge}
            </Badge>
          )}
        </div>
        {isOpen ? (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        )}
      </button>
      {isOpen && <div className="border-t">{children}</div>}
    </div>
  )
}

/**
 * Preprocesses LLM-generated markdown to fix common formatting issues
 * that cause ReactMarkdown to misparse elements:
 * - Ensures blank line before '---' to prevent setext h2 heading interpretation
 * - Ensures blank line before tables so they're not absorbed into blockquotes
 */
function preprocessMarkdown(md: string): string {
  if (!md) return md

  const lines = md.split('\n')
  const result: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const prevLine = result.length > 0 ? result[result.length - 1] : ''

    // Before --- separator: ensure blank line to prevent setext heading
    if (/^-{3,}\s*$/.test(line) && prevLine.trim() !== '') {
      result.push('')
    }

    // Before === separator: ensure blank line to prevent setext h1
    if (/^={3,}\s*$/.test(line) && prevLine.trim() !== '') {
      result.push('')
    }

    // Before table row: ensure blank line if previous is not blank/table
    if (line.startsWith('|') && !prevLine.startsWith('|') && prevLine.trim() !== '') {
      result.push('')
    }

    result.push(line)
  }

  return result.join('\n')
}

export function TranscriptDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [currentTime, setCurrentTime] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const reportRef = useRef<HTMLDivElement>(null)

  const { data: transcript, isLoading, error } = useTranscript(id || '')
  const { data: audioUrl } = useTranscriptAudioUrl(id || '')
  const reprocessMutation = useReprocessSummary()

  const handleExportPDF = useCallback(() => {
    if (!reportRef.current || !transcript) return

    const article = reportRef.current.querySelector('.digest-content')
    if (!article) return

    setIsExporting(true)

    const dateStr = new Date(transcript.start_time).toLocaleDateString('pt-BR', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    })
    const timeStr = new Date(transcript.start_time).toLocaleTimeString('pt-BR', {
      hour: '2-digit', minute: '2-digit',
    })

    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      setIsExporting(false)
      return
    }

    printWindow.document.write(`<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
<title>${transcript.room_name} - ${dateStr} ${timeStr}</title>
<style>
  @page { size: A4; margin: 18mm 15mm 20mm; }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
    color: #1a1a1a; background: #fff; line-height: 1.65; font-size: 10.5pt;
    margin: 0; padding: 0;
  }

  /* Header */
  .doc-header {
    text-align: center; padding-bottom: 14pt; margin-bottom: 18pt;
    border-bottom: 2.5pt solid #3b82f6;
  }
  .doc-header h1 { font-size: 17pt; font-weight: 700; margin: 0 0 4pt; color: #111; }
  .doc-header p { color: #6b7280; font-size: 9.5pt; margin: 2pt 0 0; }

  /* Headings */
  h1 { font-size: 16pt; font-weight: 700; margin: 22pt 0 10pt; color: #111;
       padding-bottom: 6pt; border-bottom: 2pt solid #e5e7eb; }
  h2 { font-size: 13pt; font-weight: 700; margin: 20pt 0 8pt; color: #1e293b;
       padding-left: 10pt; border-left: 3.5pt solid #3b82f6; }
  h3 { font-size: 11.5pt; font-weight: 600; margin: 14pt 0 6pt; color: #334155;
       padding-left: 8pt; border-left: 2pt solid #94a3b8; }
  h4 { font-size: 10.5pt; font-weight: 600; margin: 10pt 0 4pt; color: #334155; }

  p { margin: 5pt 0 8pt; }

  /* Tables */
  table { width: 100%; border-collapse: collapse; margin: 10pt 0 14pt; font-size: 9.5pt; }
  thead { background: #f1f5f9; }
  th { padding: 7pt 8pt; text-align: left; font-weight: 600; font-size: 8.5pt;
       text-transform: uppercase; letter-spacing: 0.4pt; color: #475569;
       border-bottom: 2pt solid #cbd5e1; }
  td { padding: 6pt 8pt; border-bottom: 1pt solid #e2e8f0; vertical-align: top; color: #334155; }
  tr:last-child td { border-bottom: none; }

  /* Blockquotes */
  blockquote {
    margin: 10pt 0; padding: 8pt 12pt;
    border-left: 3pt solid #f59e0b; background: #fffbeb;
    border-radius: 0 6pt 6pt 0; font-size: 9.5pt; color: #92400e;
  }
  blockquote p { margin: 2pt 0; }

  /* Code */
  code {
    background: #f1f5f9; padding: 1pt 4pt; border-radius: 3pt;
    font-size: 9pt; font-family: 'SF Mono', 'Consolas', monospace; color: #3b82f6;
  }

  /* Lists */
  ul, ol { margin: 5pt 0 10pt; padding-left: 18pt; }
  li { margin: 3pt 0; color: #334155; }
  li::marker { color: #3b82f6; }

  /* Separators */
  hr { border: none; border-top: 1.5pt dashed #d1d5db; margin: 16pt 0; }

  strong { font-weight: 600; color: #1e293b; }
  em { font-style: italic; color: #64748b; }

  a { color: #3b82f6; text-decoration: none; }

  /* Page break control */
  h1, h2, h3, h4 { page-break-after: avoid; }
  table { page-break-inside: avoid; }
  blockquote { page-break-inside: avoid; }
  tr { page-break-inside: avoid; }

  /* Footer */
  .doc-footer {
    position: fixed; bottom: 0; left: 0; right: 0;
    text-align: center; font-size: 8pt; color: #94a3b8;
    padding: 6pt 15mm; border-top: 1pt solid #e2e8f0;
  }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .doc-footer { position: fixed; }
  }
</style>
</head>
<body>
  <div class="doc-header">
    <h1>${transcript.room_name}</h1>
    <p>${dateStr} &middot; ${timeStr} &middot; ${transcript.participants?.length || '?'} participantes</p>
  </div>
  ${article.innerHTML}
  <div class="doc-footer">VCall &middot; Resumo Inteligente</div>
  <script>
    // Strip Tailwind classes from all elements (keep only semantic HTML)
    document.querySelectorAll('[class]').forEach(function(el) {
      if (!el.classList.contains('doc-header') && !el.classList.contains('doc-footer'))
        el.removeAttribute('class');
    });
    // Auto-print after render
    setTimeout(function() { window.print(); }, 300);
  </script>
</body></html>`)
    printWindow.document.close()

    setIsExporting(false)
  }, [transcript])

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time)
  }, [])

  const handleSegmentClick = useCallback((time: number) => {
    const audioElement = document.querySelector('audio')
    if (audioElement) {
      audioElement.currentTime = time
      setCurrentTime(time)
    }
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error || !transcript) {
    return (
      <div className="space-y-4">
        <Link to="/transcripts" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" />
          Voltar para reuniões
        </Link>
        <div className="flex items-center justify-center h-64 text-destructive">
          Erro ao carregar transcrição
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="space-y-4">
        <Link
          to="/transcripts"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para reuniões
        </Link>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="space-y-3">
            <h1 className="text-3xl font-bold tracking-tight">{transcript.room_name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5 bg-muted/50 px-3 py-1 rounded-full">
                <Calendar className="h-4 w-4" />
                {formatDateTime(transcript.start_time)}
              </span>
              <span className="flex items-center gap-1.5 bg-muted/50 px-3 py-1 rounded-full">
                <Clock className="h-4 w-4" />
                {formatDuration(transcript.duration_seconds)}
              </span>
              <span className="flex items-center gap-1.5 bg-muted/50 px-3 py-1 rounded-full">
                <Users className="h-4 w-4" />
                {transcript.participants.length} participantes
              </span>
            </div>
            {/* Participants pills */}
            <div className="flex flex-wrap gap-2">
              {transcript.participants.map((participant, index) => (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-sm"
                >
                  <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-semibold text-primary">
                    {participant.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium">{participant.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={handleExportPDF}
              disabled={isExporting || !transcript.summary}
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileDown className="h-4 w-4 mr-2" />
              )}
              {isExporting ? 'Gerando...' : 'Exportar PDF'}
            </Button>
            {audioUrl && (
              <Button variant="outline" size="sm" asChild>
                <a href={audioUrl} download>
                  <Download className="h-4 w-4 mr-2" />
                  Áudio
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main content grid */}
      <div className="grid gap-6 xl:grid-cols-4">
        {/* Left column - Summary (main focus) */}
        <div className="xl:col-span-3">
          {/* AI Summary Card - HERO */}
          <div ref={reportRef} className="rounded-2xl border-2 border-primary/20 bg-gradient-to-br from-card via-card to-primary/5 shadow-lg overflow-hidden print:shadow-none print:border print:rounded-none">
            {/* Card Header */}
            <div className="px-8 py-6 border-b border-primary/10 bg-gradient-to-r from-primary/5 to-transparent">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-2xl bg-primary/10 border border-primary/20">
                    <Sparkles className="h-7 w-7 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Resumo Inteligente</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Análise gerada automaticamente por IA
                    </p>
                  </div>
                </div>
                <Button
                  variant={transcript.summary ? "outline" : "default"}
                  size="sm"
                  disabled={reprocessMutation.isPending}
                  onClick={() => id && reprocessMutation.mutate(id)}
                >
                  {reprocessMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {reprocessMutation.isPending
                    ? 'Gerando...'
                    : transcript.summary
                      ? 'Regenerar'
                      : 'Gerar Resumo'}
                </Button>
              </div>
            </div>

            {/* Card Content - Markdown */}
            <div className="px-8 py-8">
              {transcript.summary ? (
                <article className="digest-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Headings
                      h1: ({ children }) => (
                        <h1 className="text-2xl font-bold text-foreground mb-6 pb-3 border-b-2 border-primary/30">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-xl font-bold text-foreground mt-10 mb-4 flex items-center gap-3">
                          <span className="w-1.5 h-7 bg-primary rounded-full"></span>
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-lg font-semibold text-foreground mt-6 mb-3 pl-4 border-l-2 border-muted-foreground/30">
                          {children}
                        </h3>
                      ),
                      h4: ({ children }) => (
                        <h4 className="text-base font-semibold text-foreground mt-4 mb-2">
                          {children}
                        </h4>
                      ),

                      // Paragraphs
                      p: ({ children }) => (
                        <p className="text-[15px] leading-relaxed text-foreground/90 mb-4">
                          {children}
                        </p>
                      ),

                      // Lists
                      ul: ({ children }) => (
                        <ul className="space-y-2 mb-6 ml-1">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="space-y-2 mb-6 ml-1 list-decimal list-inside">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="flex items-start gap-3 text-[15px] leading-relaxed">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0"></span>
                          <span className="text-foreground/90">{children}</span>
                        </li>
                      ),

                      // Tables
                      table: ({ children }) => (
                        <div className="overflow-x-auto mb-6 rounded-xl border border-border/50 shadow-sm">
                          <table className="w-full text-sm">
                            {children}
                          </table>
                        </div>
                      ),
                      thead: ({ children }) => (
                        <thead className="bg-muted/70 border-b border-border/50">
                          {children}
                        </thead>
                      ),
                      tbody: ({ children }) => (
                        <tbody className="divide-y divide-border/30">
                          {children}
                        </tbody>
                      ),
                      tr: ({ children }) => (
                        <tr className="hover:bg-muted/30 transition-colors">
                          {children}
                        </tr>
                      ),
                      th: ({ children }) => (
                        <th className="px-4 py-3 text-left font-semibold text-foreground text-xs uppercase tracking-wide">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="px-4 py-3 text-foreground/90">
                          {children}
                        </td>
                      ),

                      // Blockquotes (for warnings/notes)
                      blockquote: ({ children }) => (
                        <blockquote className="my-6 p-4 bg-amber-500/10 border-l-4 border-amber-500 rounded-r-xl">
                          <div className="text-amber-700 dark:text-amber-400 text-sm font-medium">
                            {children}
                          </div>
                        </blockquote>
                      ),

                      // Code
                      code: ({ children }) => (
                        <code className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono text-primary">
                          {children}
                        </code>
                      ),

                      // Horizontal rule
                      hr: () => (
                        <hr className="my-8 border-t-2 border-dashed border-muted-foreground/20" />
                      ),

                      // Strong/Bold
                      strong: ({ children }) => (
                        <strong className="font-semibold text-foreground">
                          {children}
                        </strong>
                      ),

                      // Emphasis/Italic
                      em: ({ children }) => (
                        <em className="text-muted-foreground italic">
                          {children}
                        </em>
                      ),

                      // Links
                      a: ({ href, children }) => (
                        <a
                          href={href}
                          className="text-primary hover:underline font-medium"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {children}
                        </a>
                      ),
                    }}
                  >
                    {preprocessMarkdown(transcript.summary)}
                  </ReactMarkdown>
                </article>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Sparkles className="h-16 w-16 mb-6 opacity-30" />
                  <p className="text-lg font-medium">Resumo não disponível</p>
                  <p className="text-sm mt-1">Esta reunião ainda não foi processada pela IA</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column - Collapsible panels */}
        <div className="space-y-4">
          {/* Audio Player - Collapsible */}
          <CollapsiblePanel
            title="Player de Áudio"
            icon={Volume2}
            defaultOpen={false}
            badge={formatDuration(transcript.duration_seconds)}
          >
            <div className="p-4">
              {audioUrl ? (
                <AudioPlayer
                  src={audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                />
              ) : (
                <div className="flex items-center justify-center h-20 text-muted-foreground text-sm">
                  Áudio não disponível
                </div>
              )}
            </div>
          </CollapsiblePanel>

          {/* Transcript/Roteiro - Collapsible */}
          <CollapsiblePanel
            title="Roteiro Completo"
            icon={FileText}
            defaultOpen={false}
            badge={`${transcript.segments?.length || 0} segmentos`}
          >
            <div className="p-4">
              {transcript.segments && transcript.segments.length > 0 ? (
                <TranscriptViewer
                  segments={transcript.segments}
                  currentTime={currentTime}
                  onSegmentClick={handleSegmentClick}
                  className="h-[500px]"
                />
              ) : (
                <div className="flex items-center justify-center h-20 text-muted-foreground text-sm">
                  Transcrição não disponível
                </div>
              )}
            </div>
          </CollapsiblePanel>

          {/* Metadata Card */}
          <div className="rounded-xl border bg-card p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-muted-foreground mb-4 uppercase tracking-wide">
              Informações Técnicas
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Meeting ID</span>
                <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                  {transcript.meeting_id.slice(0, 12)}...
                </code>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Modelo</span>
                <Badge variant="outline">{transcript.whisper_model}</Badge>
              </div>
              {transcript.processing_stats && (
                <>
                  <div className="pt-2 border-t">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-muted-foreground">Processamento</span>
                      <span className="font-medium">
                        {formatDuration(transcript.processing_stats.total_processing_time_sec)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Velocidade</span>
                      <Badge variant="secondary">
                        {transcript.processing_stats.realtime_factor.toFixed(1)}x realtime
                      </Badge>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
